/*
  Shared Login Form
  Used by both the Vytelle and the TOG (Transova) login experiences.
*/

import React from "react";
import { Link, useHistory } from "react-router-dom";

// Redux
import { useDispatch } from "react-redux";

// Antd
import { Form, Button, Input } from "antd";

// Components
import {
  Email,
  Password,
} from "@Vytelle/authentication/src/components/Common/FormElements";

// Constants
import APP_URL from "@Vytelle/authentication/src/constants/ApplicationUrls";
import { APP_ADMIN } from "@Vytelle/authentication/src/config/environment";

// Helper
import { getErrorMessage } from "@Vytelle/authentication/src/helpers/GeneralHelper";
import {
  errorNotification,
  successNotification,
} from "@Vytelle/authentication/src/helpers/Notification";

// Actions
import { login } from "@Vytelle/authentication/src/store/actions/AuthAction";

// Localization
import LOCALIZATION from "@Vytelle/authentication/src/services/LocalizationService";

function LoginForm({
  showSignUp = true,
  identifierType = "email",
  identifierLabel,
  identifierPlaceholder,
  showTitle = true,
  showFieldLabels = false,
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const isUsernameLogin = identifierType === "username";
  const resolvedIdentifierLabel =
    identifierLabel ||
    (isUsernameLogin ? LOCALIZATION.USERNAME : LOCALIZATION.EMAIL_ADDRESS);
  const resolvedIdentifierPlaceholder =
    identifierPlaceholder || resolvedIdentifierLabel;

  // Refs
  const [form] = Form.useForm();
  let formRef = React.createRef();

  const onSubmit = (data) => {
    const loginData = isUsernameLogin
      ? {
          username: data.username,
          email: data.username,
          password: data.password,
        }
      : data;

    dispatch(login(loginData)).then(
      (e) => {
        successNotification(LOCALIZATION.SUCCESSFULLY_LOGIN_MESSAGE);
        const data = e?.data?.result;
        history.push({
          pathname: APP_URL.APP_LANDING_SCREEN,
          state: { data },
        });
      },
      (e) => {
        errorNotification(
          getErrorMessage(e, LOCALIZATION.UN_SUCCESSFULLY_LOGIN_MESSAGE)
        );
      }
    );
  };

  return (
    <>
      {showTitle && <h2 className="sub-head">{LOCALIZATION.LOGIN}</h2>}

      <Form form={form} ref={formRef} onFinish={onSubmit} layout="vertical">
        <div className="form-group">
          {isUsernameLogin ? (
            <Form.Item
              name="username"
              label={showFieldLabels ? resolvedIdentifierLabel : undefined}
              rules={[{ required: true, message: LOCALIZATION.REQUIRED }]}
            >
              <Input
                className="form-control"
                placeholder={
                  showFieldLabels ? undefined : resolvedIdentifierPlaceholder
                }
              />
            </Form.Item>
          ) : (
            <Email
              className="form-control"
              name="email"
              label={showFieldLabels ? resolvedIdentifierLabel : undefined}
              required={true}
              placeholder={
                showFieldLabels ? undefined : resolvedIdentifierPlaceholder
              }
            />
          )}
        </div>

        <div className="form-group">
          <Password
            className="form-control"
            name="password"
            label={showFieldLabels ? LOCALIZATION.PASSWORD : undefined}
            required={true}
            minLength={1}
            placeholder={showFieldLabels ? undefined : LOCALIZATION.PASSWORD}
          />
        </div>

        <div className="link-group">
          <Link to={APP_URL.FORGOT_PASSWORD}>
            {LOCALIZATION.FORGOT_PASSWORD}?
          </Link>
          {showSignUp && (
            <a
              href={`${APP_ADMIN}${APP_URL.CLIENT_SIGN_UP}`}
              rel="noopener noreferrer"
            >
              {LOCALIZATION.CLIENT_SIGN_UP}
            </a>
          )}
        </div>

        <Form.Item>
          <Button
            block
            size="medium"
            type="primary"
            htmlType="submit"
            className="mt-4 auth-action"
          >
            {LOCALIZATION.LOGIN}
          </Button>
        </Form.Item>
      </Form>
    </>
  );
}

export default LoginForm;
